import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  Optional,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AccountService, ContentService, ContextService, MetaService } from '@app/core/services';
import { ChangableComponent } from '@app/models/changable.component';
import { setCurrentItem } from '@app/store/actions/content.actions';
import { getLibraryRouteData } from '@app/store/reducers/library.reducer';
import { Store } from '@ngrx/store';
import { ErrorNotificationService } from '@shared/error-notification/error-notification.service';
import { loadFeaturings } from '@store/actions/featuring.actions';
import { CLibraryFilterData } from '@store/models';
import { getFeaturingsByKey } from '@store/reducers/featuring.reducer';
import { Content, ContentTypeEnum, EContentOrder, EContentPaneFormat, IContentPane, User } from 'lingo2-models';
import { DeviceDetectorService } from 'ngx-device-detector';
import { Observable, of } from 'rxjs';
import { filter, first, mergeAll, takeUntil, tap } from 'rxjs/operators';
import { RESPONSE } from '../../express.tokens';
import { NotFoundError } from '../lingo2-errors';
import { LibrarySidebarService } from './sidebars/library-sidebar/library-sidebar.service';

@Component({
  selector: 'app-content-page',
  templateUrl: './content-page.component.html',
  styleUrls: ['./content-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContentPageComponent extends ChangableComponent implements OnInit, OnDestroy {
  public paneMain: IContentPane;
  public shareWidgetPane: IContentPane;
  public paneListForTeachers: IContentPane[] = [];
  public paneListForStudent: IContentPane[] = [];
  public additionalPanes404: IContentPane[] = [];
  private contentFilter404: CLibraryFilterData;

  public item: Content;

  public me: User;
  public loading = true;
  public justCreated: boolean;
  private content_slug: string;

  constructor(
    public errorNotificationService: ErrorNotificationService,
    private librarySidebarService: LibrarySidebarService,
    private route: ActivatedRoute,
    private contentService: ContentService,
    private contextService: ContextService,
    private metaService: MetaService,
    private sidebarService: LibrarySidebarService,
    private readonly store: Store,
    protected readonly cdr: ChangeDetectorRef,
    @Optional() @Inject(RESPONSE) private response: any,
    public deviceService: DeviceDetectorService,
  ) {
    super(cdr);
  }

  public ngOnInit(): void {
    of(
      this.watchMe$, // при логине/перелогине - перегрузить контент
      this.watchRouteChanged$, // При смене роута - загрузить контент по slug
    )
      .pipe(mergeAll(), takeUntil(this.destroyed$))
      .subscribe({
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });

    this.justCreated = this.route.snapshot.queryParams['created'];
    this.initSubscribe();
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.metaService.reset();
    this.librarySidebarService.disableContentPage();
  }

  private initSubscribe() {
    this.store
      .select(getLibraryRouteData)
      .pipe(
        filter((v) => !!v),
        takeUntil(this.destroyed$),
      )
      .subscribe({
        next: (data) => {
          this.contentFilter404 = data;
          this.store.dispatch(loadFeaturings({ contentFilter: this.contentFilter404 }));
          this.loadContentBlocks();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  private loadContentBlocks() {
    this.detectChanges();
    this.store
      .select(getFeaturingsByKey, { contentFilter: this.contentFilter404 })
      .pipe(first(), takeUntil(this.destroyed$))
      .subscribe({
        next: (res) => {
          this.additionalPanes404 = res?.data;
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
          this.detectChanges();
        },
      });
  }

  /**
   * При логине/перелогине - права пользователя могут поменяться
   */
  private get watchMe$(): Observable<User> {
    // при логине/перелогине - права пользователя могут поменяться
    return this.contextService.me$.pipe(
      tap((me) => {
        this.me = me;
      }),
    );
  }

  /**
   * При смене роута - загрузить контент по slug
   */
  private get watchRouteChanged$(): Observable<any> {
    return this.store.select(getLibraryRouteData).pipe(
      tap((data) => {
        this.loadContent(data?.item);
      }),
    );
  }

  public get paneList(): IContentPane[] {
    if (this.item.type === ContentTypeEnum.microtext) {
      return [];
    }
    return this.isTeacher ? this.paneListForTeachers : this.paneListForStudent;
  }

  public get isTeacher(): boolean {
    return AccountService.isAsIfTeacher(this.me);
  }

  public trackByFn(index: number, item: IContentPane): number {
    return item.order;
  }

  private initPanes() {
    this.paneMain = {
      order: 0,
      format: EContentPaneFormat['card-full-main'],
      loaded: true,
      content: [this.item],
      add_link: 'null',
    };

    this.shareWidgetPane = this.justCreated
      ? {
          order: 0,
          format: EContentPaneFormat['share-link-widget'],
          loaded: true,
          title: 'library.share-link-widget.title2',
          subtitle: 'library.share-link-widget.text2',
        }
      : {
          order: 0,
          format: EContentPaneFormat['share-link-widget'],
          loaded: true,
          title: 'library.share-link-widget.title1',
          subtitle: 'library.share-link-widget.text1',
        };

    this.paneListForTeachers = [
      {
        order: 1,
        format: EContentPaneFormat['new-grid-universal-card'],
        loaded: false,
        new_grid_view: 'vertical',
        show_like_slider: true,
        max_element_count: 7,
        max_element_width: 250,
        default_element_width: 250,
        title: 'library.titles.similar',
        routerUrlCreate: '',
        contentFilter: {
          is_library: true,
          related_to: this.item.id,
          limit: 20,
        },
        rel: 'similar', // используется в тестах
      },
      {
        order: 2,
        format: EContentPaneFormat['new-exercises'],
        loaded: false,
        title: 'library.titles.lessons-from-teacher',
        new_grid_view: 'vertical-square',
        default_element_width: 250,
        contentFilter: {
          is_library: true,
          author_id: this.item.author_id,
          order: EContentOrder.viewed_desc,
          limit: 20,
        },
        rel: 'same-author', // используется в тестах
      },
      {
        order: 3,
        format: EContentPaneFormat['new-grid-universal-card'],
        loaded: false,
        new_grid_view: 'vertical',
        show_like_slider: true,
        max_element_count: 7,
        max_element_width: 250,
        default_element_width: 250,
        title: 'library.titles.recently-viewed',
        routerUrlCreate: '',
        contentFilter: {
          is_library: true,
          order: EContentOrder.viewed_desc,
          limit: 20,
        },
        rel: 'recently-viewed', // используется в тестах
      },
    ];

    this.paneListForStudent = [
      {
        order: 1,
        format: EContentPaneFormat['new-grid-universal-card'],
        loaded: false,
        new_grid_view: 'vertical',
        show_like_slider: true,
        max_element_count: 7,
        max_element_width: 250,
        default_element_width: 250,
        title: 'library.titles.similar',
        routerUrlCreate: '',
        contentFilter: {
          is_library: true,
          related_to: this.item.id,
          limit: 20,
        },
        rel: 'similar', // используется в тестах
      },
      {
        order: 2,
        format: EContentPaneFormat['new-exercises'],
        loaded: false,
        title: 'library.titles.lessons-from-teacher',
        new_grid_view: 'vertical-square',
        default_element_width: 250,
        contentFilter: {
          is_library: true,
          author_id: this.item.author_id,
          limit: 20,
        },
        rel: 'same-author', // используется в тестах
      },
      {
        order: 3,
        format: EContentPaneFormat['new-grid-universal-card'],
        loaded: false,
        new_grid_view: 'vertical',
        show_like_slider: true,
        max_element_count: 7,
        max_element_width: 250,
        default_element_width: 250,
        title: 'library.titles.recently-viewed',
        routerUrlCreate: '',
        contentFilter: {
          is_library: true,
          order: EContentOrder.viewed_desc,
          limit: 20,
        },
        rel: 'recently-viewed', // используется в тестах
      },
    ];
  }

  protected loadContent(content_slug: string) {
    if (!content_slug) {
      return;
    }
    if (this.loading && content_slug === this.content_slug) {
      return;
    }

    this.content_slug = content_slug;
    this.loading = true;
    this.markForCheck();

    this.contentService
      .getContentBySlug(this.content_slug, true)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (item: Content) => {
          this.item = item;
          this.sidebarService.setUser(item.author);
          this.initPanes();
          this.librarySidebarService.enableContentPage();
          this.sidebarService.disableFilter();
          this.loading = false;

          this.store.dispatch(setCurrentItem({ item }));
          this.markForCheck();
        },
        error: (error: any) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
          this.loading = false;
          this.markForCheck();
        },
      });
  }

  public handleError(err) {
    if (err instanceof NotFoundError) {
      this.metaService.setTitle(['Publication not found']);
      // TODO remove metaService
      if (this.response) {
        this.response.statusCode = 404;
        this.response.statusMessage = 'Page Not Found';
      }
    }
  }
}
